"use strict";
import {createLogger} from "@gongt/ts-stl-library/log/debug";
import {LOG_LEVEL} from "@gongt/ts-stl-library/log/levels";
import * as vm from "vm";
import {
	IUserPermissionDesc,
	UserPermissionClass,
	UserPermissionConstructor,
	UserPermissionSerialize,
} from "../bridge/define.permission";

const debug = createLogger(LOG_LEVEL.DEBUG, 'permission');

export type IPermDataArg = UserPermissionSerialize|UserPermissionClass;

export function permDataObject(perm: IPermDataArg): UserPermissionSerialize {
	if (perm['toJSON']) {
		return (perm as UserPermissionClass).toJSON();
	} else {
		return perm as UserPermissionSerialize;
	}
}

export abstract class AbstractUserPermission implements UserPermissionClass {
	protected _scope: string;
	protected _name: string;
	protected _available: number;
	protected _expire: number;
	
	static readonly DEFAULT_SCOPE = '';
	static readonly ANY_SCOPE = '';
	
	abstract get project(): string;
	
	get description(): IUserPermissionDesc {
		return null;
	}
	
	constructor(scope: string = AbstractUserPermission.DEFAULT_SCOPE) {
		this._name = this.constructor.name;
		this._scope = scope || '';
	}
	
	/** @deprecated */
	get className() {
		return this._name;
	}
	
	get permName() {
		return this._name;
	}
	
	get available() {
		return this._available || 0;
	}
	
	setAvailable(v: number|Date) {
		if (typeof v === 'number') {
			this._available = v;
		} else {
			this._available = v.getTime();
		}
	}
	
	get expire(): number {
		return this._expire || 0;
	}
	
	setExpire(v: number|Date) {
		if (typeof v === 'number') {
			this._expire = v;
		} else {
			this._expire = v.getTime();
		}
	}
	
	get scope() {
		return this._scope;
	}
	
	setScope(scope: string) {
		if (typeof scope !== 'string') {
			throw new TypeError('permObj.setScope(* not a string *)')
		}
		this._scope = scope;
	}
	
	get status() {
		const exp = this._expire;
		if (exp) {
			if (exp < Date.now()) {
				return 1;
			}
		}
		const ava = this._available;
		if (ava) {
			if (ava > Date.now()) {
				return -1;
			}
		}
		return 0;
	}
	
	toJSON(): UserPermissionSerialize {
		return {
			available: this._available,
			expire: this._expire,
			permName: this._name,
			scope: this._scope,
			project: this.project || '*',
		}
	}
}

export abstract class GlobalUserPermission extends AbstractUserPermission {
	get project() {
		return undefined;
	}
}

const lno = 4 + parseInt(/:(\d+):\d+\)$/.exec((new Error).stack.split('\n')[1].trim())[1]);

export function createClassDynamic(name: string, base: UserPermissionConstructor): UserPermissionConstructor {
	let ret = {ret: null};
	try {
		const ctx = vm.createContext({ret: ret, [base.name]: base});
		vm.runInContext(`class ${name} extends ${base.name} {}
ret.ret = ${name}; `, ctx, {
			filename: __filename,
			lineOffset: lno,
			columnOffset: 2,
			displayErrors: true,
		});
		return <any>ret.ret;
	} catch (e) {
		throw new TypeError(`can no create class "${name}": ${e.message}`)
	}
}

export function createProjectAbstractPermission(projectName: string): UserPermissionConstructor {
	return class ScopedAbstractUserPermission extends AbstractUserPermission {
		constructor(scope: string = ScopedAbstractUserPermission.DEFAULT_SCOPE) {
			super(scope);
			if (this.constructor === ScopedAbstractUserPermission) {
				throw new TypeError("can not create instance of abstract class `ScopedAbstractUserPermission'")
			}
		}
		
		get project() {
			return projectName;
		}
	};
}
